import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import NumberFlow from '@number-flow/react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useDate } from 'root/src/client/v2/common/hooks/useDate';
import { Card } from 'root/src/client/v2/common/components/ui/card';
import { Badge } from 'root/src/client/v2/common/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from 'root/src/client/v2/common/components/ui/tooltip';
import { useCreateGiveawayFormContext } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/contexts/createGiveawayForm.context';
const formatShort = (date) => `${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
})}, ${date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
})}`;
const formatFull = (date) => date.toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
});
const DateBadge = memo(({ date }) => (<Tooltip>
		<TooltipTrigger asChild>
			<Badge variant="secondary" tabIndex={0} className="cursor-default font-medium">{formatShort(date)}</Badge>
		</TooltipTrigger>
		<TooltipContent>{formatFull(date)}</TooltipContent>
	</Tooltip>));
// Live preview of when the upcoming giveaway starts / ends (winner selection), plus the series end
// when repeating. Starts now (or when the active giveaway ends), and runs for the chosen duration.
// Dates show a short label in a badge, with the full date on hover.
export const RedesignSchedulePreview = memo(() => {
    var _a;
    const now = useDate();
    const form = useFormContext();
    const { giveaway } = useCreateGiveawayFormContext();
    const durationTime = form.watch('durationTime');
    const repeatTimes = form.watch('repeatTimes');
    const isRepeatingEnabled = form.watch('isRepeatingEnabled');
    if (!durationTime) {
        return null;
    }
    const start = new Date((_a = giveaway === null || giveaway === void 0 ? void 0 : giveaway.endDate) !== null && _a !== void 0 ? _a : now);
    const end = new Date(start.getTime() + durationTime);
    const isRepeating = Boolean(isRepeatingEnabled && repeatTimes);
    const lastEnd = isRepeating ? new Date(start.getTime() + durationTime * ((repeatTimes !== null && repeatTimes !== void 0 ? repeatTimes : 0) + 1)) : undefined;
    return (<TooltipProvider delayDuration={150}>
			<Card className="border-white/[0.08] bg-white/[0.02] p-4 shadow-none">
				<div className="mb-3 flex items-center gap-2">
					<CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} className="text-neon-cyan"/>
					<span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Schedule</span>
				</div>

				<div className="flex items-center justify-between gap-4">
					<div className="flex flex-col items-start gap-1.5">
						<p className="text-[11px] uppercase tracking-wider text-muted-foreground">Starts</p>
						<DateBadge date={start}/>
					</div>
					<ArrowRightAltIcon sx={{ fontSize: 22 }} className="shrink-0 text-neon-cyan/60"/>
					<div className="flex flex-col items-end gap-1.5">
						<p className="text-[11px] uppercase tracking-wider text-muted-foreground">Ends</p>
						<DateBadge date={end}/>
					</div>
				</div>

				{isRepeating && lastEnd && (<p className="mt-3 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-xs text-muted-foreground">
						Repeats
						<span className="font-semibold text-foreground"><NumberFlow value={repeatTimes !== null && repeatTimes !== void 0 ? repeatTimes : 0}/>x</span>
						, last ends
						<DateBadge date={lastEnd}/>
					</p>)}
			</Card>
		</TooltipProvider>);
});
